import { useEffect, useState } from 'react';
import { MapPin, Phone, ArrowUpRight, Send, CheckCircle2 } from 'lucide-react';
import Reveal from './Reveal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  'w-full bg-transparent border border-navy/20 px-4 py-3.5 text-navy font-light text-sm placeholder:text-navy/35 focus:outline-none focus:border-gold transition-colors duration-300';

const emptyForm = { name: '', email: '', telephone: '', message: '', company: '' };

export default function Contact() {
  const [csrf, setCsrf] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle');
  const [formError, setFormError] = useState('');

  const fetchToken = async () => {
    try {
      const res = await fetch(`${API}/contact/csrf`);
      const data = await res.json();
      setCsrf(data.csrf_token);
    } catch {
      setFormError('Unable to prepare the form. Please reload the page.');
    }
  };

  useEffect(() => {
    fetchToken();
  }, []);

  const onChange = (key) => (e) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    setErrors({});
    setFormError('');
    try {
      const res = await fetch(`${API}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, csrf_token: csrf }),
      });
      if (res.ok) {
        setStatus('success');
        setForm(emptyForm);
        fetchToken();
        return;
      }
      const data = await res.json().catch(() => ({}));
      if (data.detail && typeof data.detail === 'object') {
        setErrors(data.detail);
        if (data.detail.csrf_token) fetchToken();
      } else {
        setFormError(typeof data.detail === 'string' ? data.detail : 'Something went wrong. Please try again or call us.');
      }
      setStatus('idle');
    } catch {
      setFormError('Network error. Please try again or call us at 919-967-5733.');
      setStatus('idle');
    }
  };

  const fieldError = (key) =>
    errors[key] ? (
      <p data-testid={`contact-error-${key}`} className="mt-2 text-xs text-red-800 tracking-wide">
        {errors[key]}
      </p>
    ) : null;

  return (
    <section id="contact" data-testid="contact-section" className="bg-sand py-28 lg:py-40 border-t border-navy/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-16">
        <div className="lg:col-span-7">
          <Reveal>
            <p className="text-xs tracking-[0.35em] uppercase text-gold mb-6">
              <span className="eyebrow-rule" />
              Contact
            </p>
            <h2 data-testid="contact-heading" className="font-serif tracking-tight text-navy text-4xl sm:text-5xl lg:text-6xl leading-[1.08]">
              Begin the <span className="italic text-gold">conversation.</span>
            </h2>
            <p className="mt-8 max-w-md text-slateink font-light leading-relaxed text-base">
              Send us a note below and we will be in touch — or simply call <a href="tel:+19199675733" className="text-navy border-b border-gold/50 hover:text-gold transition-colors duration-300">919-967-5733</a>.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            {status === 'success' ? (
              <div data-testid="contact-success-message" className="mt-12 border border-gold/50 bg-cream p-10">
                <CheckCircle2 size={28} className="text-gold" aria-hidden="true" />
                <h3 className="font-serif text-navy text-2xl tracking-tight mt-5">Thank you — your message is on its way.</h3>
                <p className="mt-4 text-slateink font-light text-sm leading-relaxed">
                  We have received your enquiry and will respond shortly. If your matter is urgent, please call us at 919-967-5733.
                </p>
                <button
                  type="button"
                  data-testid="contact-send-another-button"
                  onClick={() => setStatus('idle')}
                  className="mt-8 text-xs tracking-[0.2em] uppercase text-navy border-b border-gold/50 pb-1 transition-colors duration-300 hover:text-gold"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form data-testid="contact-form" onSubmit={onSubmit} noValidate className="mt-12 space-y-6 max-w-xl">
                <input
                  type="text"
                  name="company"
                  value={form.company}
                  onChange={onChange('company')}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="absolute -left-[9999px] -top-[9999px] h-0 w-0 opacity-0"
                />
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="contact-name" className="block text-xs tracking-[0.2em] uppercase text-navy/70 mb-3">
                      Name <span className="text-gold">*</span>
                    </label>
                    <input
                      id="contact-name"
                      data-testid="contact-name-input"
                      type="text"
                      required
                      value={form.name}
                      onChange={onChange('name')}
                      placeholder="Your full name"
                      className={inputCls}
                    />
                    {fieldError('name')}
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-xs tracking-[0.2em] uppercase text-navy/70 mb-3">
                      Email <span className="text-gold">*</span>
                    </label>
                    <input
                      id="contact-email"
                      data-testid="contact-email-input"
                      type="email"
                      required
                      value={form.email}
                      onChange={onChange('email')}
                      placeholder="you@example.com"
                      className={inputCls}
                    />
                    {fieldError('email')}
                  </div>
                </div>
                <div>
                  <label htmlFor="contact-telephone" className="block text-xs tracking-[0.2em] uppercase text-navy/70 mb-3">
                    Telephone <span className="normal-case tracking-normal text-navy/40">(optional)</span>
                  </label>
                  <input
                    id="contact-telephone"
                    data-testid="contact-phone-input"
                    type="tel"
                    value={form.telephone}
                    onChange={onChange('telephone')}
                    placeholder="919-555-0123"
                    className={inputCls}
                  />
                  {fieldError('telephone')}
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-xs tracking-[0.2em] uppercase text-navy/70 mb-3">
                    Message <span className="text-gold">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    data-testid="contact-message-input"
                    required
                    rows={5}
                    value={form.message}
                    onChange={onChange('message')}
                    placeholder="How can we help?"
                    className={`${inputCls} resize-y`}
                  />
                  {fieldError('message')}
                </div>
                {formError && (
                  <p data-testid="contact-form-error" className="text-xs text-red-800 tracking-wide">{formError}</p>
                )}
                <button
                  type="submit"
                  data-testid="contact-submit-button"
                  disabled={status === 'sending' || !csrf}
                  className="group inline-flex items-center gap-3 bg-gold text-navy text-xs tracking-[0.2em] uppercase px-8 py-4 transition-colors duration-300 hover:bg-gold-light disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {status === 'sending' ? 'Sending…' : 'Send message'}
                  <Send size={14} className="transition-transform duration-300 group-hover:translate-x-0.5" aria-hidden="true" />
                </button>
              </form>
            )}
          </Reveal>
        </div>

        <div className="lg:col-span-5">
          <Reveal delay={0.15}>
            <div className="border border-navy/15 bg-cream p-10 lg:p-12 h-full flex flex-col">
              <MapPin size={22} className="text-gold" aria-hidden="true" />
              <h3 className="font-serif text-navy text-2xl tracking-tight mt-6">Visit the office</h3>
              <address data-testid="contact-address" className="not-italic mt-5 text-slateink font-light leading-relaxed text-base">
                A. Grant Mangum &amp; Co., Inc.<br />
                100 Timberhill Place, Unit 120<br />
                Chapel Hill, NC 27514
              </address>
              <p className="mt-6 text-xs tracking-[0.2em] uppercase text-slateink/70">
                Monday – Thursday, by appointment only
              </p>
              <a
                href="tel:+19199675733"
                data-testid="contact-phone-link"
                className="group inline-flex items-baseline gap-3 mt-10 font-serif text-navy text-3xl tracking-tight transition-colors duration-300 hover:text-gold"
              >
                919-967-5733
                <Phone size={20} className="text-gold transition-transform duration-300 group-hover:rotate-12" aria-hidden="true" />
              </a>
              <a
                href="https://maps.google.com/?q=100+Timberhill+Place+Unit+120+Chapel+Hill+NC+27514"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-map-link"
                className="group mt-auto pt-10 inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-navy border-b border-gold/50 pb-1 self-start transition-colors duration-300 hover:text-gold"
              >
                View on map
                <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" aria-hidden="true" />
              </a>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
